import { z } from "zod";

import { ErrorMessages } from "@/app/_module/constants";
import { RAW_PHONE_REGEX, isValidUSPhoneNumber } from "@/app/_module/utils/formatters";
import { checkPersonName } from "@/app/_module/utils/personName";
import { checkUsPhone } from "@/app/_module/utils/usPhone";

// USA phone number regex (10 digits, optional +1)
const phoneRegex = /^(\+1)?[2-9]\d{2}[2-9]\d{2}\d{4}$/;

// NPN regex (7-11 digits — some producers have 7-digit NPNs; extra headroom)
const npnRegex = /^\d{7,11}$/;

export const signupSchema = z.object({
  // Account info (matches users table)
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    // Special-char class kept intentionally broad and aligned with the live
    // requirements checklist on the signup page so users don't see all-green
    // checkmarks while the form silently stays invalid.
    .regex(/^\S+$/, "Password cannot contain spaces")
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?])/,
      "Password must include an uppercase letter, a lowercase letter, a number, and a special character"
    )
    .optional(), // Optional for social auth
  name: z
    .string()
    .min(1, "Name is required")
    // First AND last name. Mirrors the server gate on /auth/register so the
    // form never accepts something the API will reject.
    .superRefine((val, ctx) => {
      const res = checkPersonName(val);
      if (!res.valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: res.reason || "Please enter your first and last name.",
        });
      }
    }),
  contact_phone: z
    .string()
    .min(1, "Phone number is required")
    // Real US number, not just ten digits — mirrors the server gate so the form
    // never accepts something /auth/register will reject.
    .superRefine((val, ctx) => {
      const res = checkUsPhone(val);
      if (!res.valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: res.reason || "Enter a valid US phone number",
        });
      }
    }),
  user_type: z.enum(["agent", "admin", "owner"], {
    message: "Please select a valid user type",
  }).optional().default("agent"), // Default to agent
  npn: z
    .string()
    .min(1, "NPN is required")
    .regex(npnRegex, "NPN must be 7-11 digits")
    .refine((val) => val.length >= 7 && val.length <= 11, {
      message: "NPN must be between 7-11 digits",
    }),
  npnState: z
    .string()
    .min(1, "State is required for NPN verification")
    .max(2, "State must be a 2-character code"),

  // Social auth
  socialProvider: z.enum(["google", "apple", "microsoft"]).optional(),
  socialId: z.string().optional(),
  
  // Plan selection (maps to account_type)
  selectedPlan: z.enum(["basic", "full_fledged"]).optional(),
  
  // Trial selection
  requestedTrialType: z.enum(["basic", "full_fledged"]).optional(),
  
  // Agency (premium only)
  agencyName: z.string().optional(),
  agencyDescription: z.string().optional(),
  agencyContactEmail: z.string().email().optional(),
  requestedRole: z.enum(["agent", "agency_owner"]).optional(),
  
  // Security
  turnstileToken: z.string().optional(),
  
  // Invite-related fields
  inviteToken: z.string().optional(),
  isInviteSignup: z.boolean().optional(),
  agencyId: z.string().optional(),
  
  // Area code selection
  selectedAreaCode: z.number().optional(),
  selectedState: z.string().optional(),
  
  // Terms and conditions
  /**
   * Typed e-signature. Required once the Terms are accepted — the checkbox alone
   * is an ambiguous act, and E-SIGN turns on demonstrable INTENT to sign.
   * Cross-field rule lives in the schema-level refine below so the message can
   * point at this field rather than at the checkbox.
   */
  termsSignature: z.string().trim().optional(),

  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "You must accept the Terms and Conditions to continue",
  }),
})
  // A signature is only meaningful once the box is ticked, so the rule is
  // conditional rather than a plain required field — otherwise someone who has
  // not yet agreed sees a signature error, which reads as nonsense.
  .refine(
    (d) => !d.acceptTerms || (d.termsSignature ?? '').trim().length >= 2,
    {
      path: ['termsSignature'],
      message: 'Type your full legal name to sign',
    },
  );

export type TSignupSchema = z.infer<typeof signupSchema>;
